/**
 * oj-polo-pack.js — <oj-polo-pack>
 *
 * Behaviour for sections/oj-polo-pack.liquid, ported from stuburt-prod's
 * assets/stuburt-pack-stage.js (the polo pack page) and cut down to the one
 * shape this page needs: A READY-MADE PACK of three (operator, 2026-09-24 —
 * "too many clicks for an impulse buyer"; replaced a same-day tray build).
 *
 * TWO TAPS: a size in the gate (which sets all three and closes itself), then
 * "Add all three", which goes straight to checkout. The three polos are
 * already chosen — slot i opens on style i in its starting colour.
 * Everything else is optional and lives on the polo itself:
 *   - colour swatches (every colour);
 *   - size boxes (every size; out of stock in the chosen colour = greyed and
 *     crossed out, not pickable);
 *   - "Swap style", cycling the slot Kenson -> Mallory -> Regent, so three the
 *     same or any mix is possible ("order any 2, get the third free").
 * The CHEAPEST of the three is labelled free — never chosen by the customer —
 * because that is what native Buy X Get Y does at checkout.
 *
 * NO MONEY IS FORMATTED HERE. Every price string arrives server-rendered in
 * the section's JSON (per style, and per slot combination for the totals);
 * this file only picks which string to show.
 *
 * CART: one POST /cart/add.js with the three real variants, then straight to
 * /checkout (operator, 2026-09-24: no drawer — one less tap). Checkout shows
 * the Buy X Get Y discount on the free polo.
 */

// v3: the ready-made pack. A new key so an earlier model's state is ignored.
const STORE_KEY = 'oj:polo-pack:v3';
const SLOTS = 3;

const SIZE_ORDER = ['xxxs', 'xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl', 'xxxxl'];
const SIZE_ALIASES = {
  small: 's',
  medium: 'm',
  large: 'l',
  'x large': 'xl',
  'extra large': 'xl',
  'xx large': 'xxl',
  'xxx large': 'xxxl',
  'xxxx large': 'xxxxl',
};

/** "XX-Large" -> "xxl"; unknown values pass through lowercased. */
const sizeKey = (value) => {
  const k = String(value || '').toLowerCase().replace(/[-_.]/g, ' ').replace(/\s+/g, ' ').trim();
  return SIZE_ALIASES[k] || k;
};
/** The PDP's own abbreviation (S, M, L, XL, XXL, XXXL). */
const sizeLabel = (value) => {
  const k = sizeKey(value);
  return SIZE_ORDER.includes(k) ? k.toUpperCase() : String(value || '');
};
const sizeRank = (value) => {
  const i = SIZE_ORDER.indexOf(sizeKey(value));
  return i < 0 ? 99 : i;
};

const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const fileOf = (url) => String(url || '').split('?')[0].split('/').pop();

class OjPoloPack extends HTMLElement {
  connectedCallback() {
    this.styles = this.readJson('script[data-styles]', []);
    this.combos = this.readJson('script[data-combos]', {});
    if (!Array.isArray(this.styles) || !this.styles.length) return;

    this.gate = this.querySelector('[data-gate]');
    this.addBtn = this.querySelector('[data-add]');
    this.errorEl = this.querySelector('[data-error]');
    this.cards = Array.from(this.querySelectorAll('[data-slot]'));

    // Per SLOT: its style (index into styles), colour and own size override.
    // Slot i starts on style i, matching the server render.
    this.state = {
      size: '',
      style: Array.from({ length: SLOTS }, (_, i) => i % this.styles.length),
      colour: {},
      override: {},
    };
    this.needSize = {};

    this.abort = new AbortController();
    this.restore();
    this.buildGate();
    this.bind();
    this.paint();

    // The editor re-renders this section constantly; a modal that re-opens on
    // every setting change would make the page uneditable.
    if (!this.state.size && !window.Shopify?.designMode) this.openGate();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.bannerWatch?.disconnect();
    this.unlock();
  }

  readJson(selector, fallback) {
    try {
      return JSON.parse(this.querySelector(selector)?.textContent || 'null') ?? fallback;
    } catch {
      return fallback;
    }
  }

  /* ---------- persistence ------------------------------------------- */

  restore() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(`${STORE_KEY}:${this.id}`) || '{}');
      // Only restore what the page still offers: stock moves between visits.
      if (saved.size && this.allSizes().includes(saved.size)) this.state.size = saved.size;
      if (saved.colour) this.state.colour = saved.colour;
      if (Array.isArray(saved.style)) {
        saved.style.forEach((x, i) => {
          if (i < SLOTS && this.styles[x]) this.state.style[i] = x;
        });
      }
      if (saved.override) this.state.override = saved.override;
    } catch {
      /* first visit */
    }
  }

  save() {
    try {
      sessionStorage.setItem(`${STORE_KEY}:${this.id}`, JSON.stringify(this.state));
    } catch {
      /* private mode: works, just re-asks next time */
    }
  }

  /* ---------- the model --------------------------------------------- */

  styleOf(slot) {
    return this.styles[this.state.style[slot]] || this.styles[0];
  }

  /** Every size at least one style ships in any colour, in size order. */
  allSizes() {
    const set = new Set();
    for (const st of this.styles) {
      for (const v of st.variants || []) if (v.available && v.size) set.add(v.size);
    }
    return [...set].sort((a, b) => sizeRank(a) - sizeRank(b));
  }

  /** This polo's size: its own pick, else the gate's. An own pick of '' means
   *  "none" — set when a colour change left the old size out of stock. */
  sizeFor(slot) {
    return Object.hasOwn(this.state.override, slot) ? this.state.override[slot] : this.state.size;
  }

  /** Sizes in stock for this polo in a colour (the chosen one by default). */
  inStock(slot, colour = this.state.colour[slot]) {
    return new Set(
      (this.styleOf(slot).variants || []).filter((v) => v.available && v.colour === colour).map((v) => v.size)
    );
  }

  /** The chosen size can't ship in the chosen colour. */
  missing(slot) {
    const size = this.sizeFor(slot);
    return Boolean(size) && !this.inStock(slot).has(size);
  }

  /** A colour that ships `size`, the starting colour first; null if none. */
  colourWith(slot, size) {
    const style = this.styleOf(slot);
    const names = (style.colours || []).map((c) => c.name).filter((n) => this.inStock(slot, n).has(size));
    return names.includes(style.defaultColour) ? style.defaultColour : names[0] || null;
  }

  /** Fills a colour only when none is set (or it no longer exists) — never
   *  overrides one the customer picked. Prefers a colour that ships the
   *  chosen size, then the starting colour, then any colour with stock. */
  settleColour(slot) {
    const style = this.styleOf(slot);
    const names = (style.colours || []).map((c) => c.name);
    if (!names.length || names.includes(this.state.colour[slot])) return;
    const size = this.sizeFor(slot);
    const stocked = names.filter((n) => this.inStock(slot, n).size);
    const def = style.defaultColour;
    this.state.colour[slot] =
      (size && this.colourWith(slot, size)) ||
      (stocked.includes(def) ? def : stocked[0]) ||
      (names.includes(def) ? def : names[0]);
  }

  resolve(slot) {
    const size = this.sizeFor(slot);
    if (!size) return null;
    const colour = this.state.colour[slot];
    return (
      (this.styleOf(slot).variants || []).find((v) => v.available && v.colour === colour && v.size === size) || null
    );
  }

  combo() {
    return this.combos[this.state.style.join('-')] || null;
  }

  /* ---------- events ------------------------------------------------ */

  bind() {
    const { signal } = this.abort;

    this.addEventListener(
      'click',
      (e) => {
        const t = e.target;
        const gateOpt = t.closest('[data-gate-opts] .oj-pack__opt');
        if (gateOpt) return this.pickGate(gateOpt);
        if (t.closest('[data-skip]')) return this.closeGate();
        if (t.closest('[data-add]')) return this.add();

        const card = t.closest('[data-slot]');
        if (!card) return;
        const slot = Number(card.dataset.slot);
        const sizeOpt = t.closest('[data-sizes] .oj-pack__opt');
        if (sizeOpt) return this.pickSize(slot, sizeOpt);
        if (t.closest('[data-swap]')) return this.swapStyle(slot);
        const swatch = t.closest('[data-swatch]');
        if (swatch) return this.pickColour(slot, swatch.dataset.colour || '');
        const nav = t.closest('[data-nav]');
        if (nav) return this.nudge(card, Number(nav.dataset.nav) || 1);
      },
      { signal }
    );

    this.gate?.addEventListener('click', (e) => e.target === this.gate && this.closeGate(), { signal });

    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape' && this.gate?.dataset.open === 'true') this.closeGate();
      },
      { signal }
    );
  }

  /* ---------- the gate ---------------------------------------------- */

  buildGate() {
    const host = this.querySelector('[data-gate-opts]');
    if (!host) return;
    host.replaceChildren(
      ...this.allSizes().map((value) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'oj-pack__opt';
        btn.dataset.value = value;
        btn.setAttribute('aria-pressed', 'false');
        btn.setAttribute('aria-label', value);
        btn.textContent = sizeLabel(value);
        return btn;
      })
    );
  }

  openGate() {
    if (!this.gate) return;
    this.gate.dataset.open = 'true';
    document.documentElement.style.overflow = 'hidden';
    this.lockedByGate = true;
    this.watchBanner();
    const first = this.gate.querySelector('.oj-pack__opt[aria-pressed="true"]') || this.gate.querySelector('.oj-pack__opt');
    window.setTimeout(() => first?.focus({ preventScroll: true }), reduced() ? 0 : 240);
  }

  closeGate() {
    if (!this.gate) return;
    this.gate.dataset.open = 'false';
    this.unlock();
  }

  /** Shopify's cookie-consent banner is a fixed, max-z-index sheet at the foot
   *  of the viewport that nothing in the theme can sit above. On a phone it
   *  covered the gate's size row, so taps hit the banner (operator,
   *  2026-09-25). Its height goes into --oj-gate-reserve as bottom padding, so
   *  the card centres in the space the banner leaves (see the gate CSS). The
   *  banner is injected after load and removed or hidden on Accept/Decline,
   *  so this is re-measured on body changes, banner changes and resize. */
  reserveBanner() {
    if (!this.gate) return;
    let reserve = 0;
    for (const el of document.querySelectorAll('[id^="shopify-pc__banner"]:not(link):not(style)')) {
      if (this.bannerWatch && !this.watched.has(el)) {
        this.watched.add(el);
        this.bannerWatch.observe(el, { attributes: true, childList: true, subtree: true });
      }
      const cs = getComputedStyle(el);
      if (cs.position !== 'fixed' || cs.display === 'none' || cs.visibility === 'hidden') continue;
      const r = el.getBoundingClientRect();
      // Only a sheet anchored in the lower half of the viewport takes space,
      // and only as much as it actually intrudes on the card: a centred card
      // ends at (vh + cardH) / 2; with reserve x it ends at (vh - x + cardH) / 2,
      // which must sit 1rem above the banner. On a desktop viewport that is
      // zero, so the card does not jump when the banner arrives.
      if (r.height > 0 && r.top > window.innerHeight / 2) {
        const card = this.gate.querySelector('.oj-pack__card');
        const cardH = card ? card.getBoundingClientRect().height : 0;
        const need = Math.ceil(window.innerHeight + cardH + 32 - 2 * r.top);
        reserve = Math.max(reserve, Math.min(Math.max(need, 0), Math.round(window.innerHeight - r.top)));
      }
    }
    this.gate.style.setProperty('--oj-gate-reserve', `${reserve}px`);
  }

  watchBanner() {
    if (this.bannerWatch) return;
    this.watched = new WeakSet();
    this.bannerWatch = new MutationObserver(() => this.reserveBanner());
    this.bannerWatch.observe(document.body, { childList: true });
    window.addEventListener('resize', () => this.reserveBanner(), { signal: this.abort.signal });
    this.reserveBanner();
  }

  unlock() {
    if (this.lockedByGate) document.documentElement.style.overflow = '';
    this.lockedByGate = false;
  }

  pickGate(btn) {
    // A gate answer is the customer changing their mind about all three, so it
    // clears the per-polo overrides — otherwise the gate would appear to do
    // nothing on a card that had been adjusted.
    this.state.size = btn.dataset.value || '';
    this.state.override = {};
    // The size is the answer that matters here, so a card whose colour doesn't
    // come in it moves to one that does (starting colour first). A card with
    // no colour in that size keeps its colour and says so under its sizes.
    for (let slot = 0; slot < SLOTS; slot += 1) {
      if (!this.missing(slot)) continue;
      const colour = this.colourWith(slot, this.state.size);
      if (colour) this.state.colour[slot] = colour;
    }
    this.paint();
    this.save();
    // One tap is the whole question: no confirm button (operator, 2026-09-24).
    this.closeGate();
  }

  /* ---------- per-card sizes -------------------------------------- */

  /** Every size this style is made in, in size order, stock or not — the ones
   *  out of stock in the chosen colour are greyed and crossed out, not hidden
   *  (operator, 2026-09-24), the same as the PDP. */
  sizesFor(slot) {
    const values = [];
    for (const v of this.styleOf(slot).variants || []) {
      if (v.size && !values.includes(v.size)) values.push(v.size);
    }
    return values.sort((a, b) => sizeRank(a) - sizeRank(b));
  }

  pickSize(slot, btn) {
    if (btn.getAttribute('aria-disabled') === 'true') return;
    this.state.override[slot] = btn.dataset.value || '';
    delete this.needSize[slot];
    this.showError('');
    this.paint();
    this.save();
  }

  paintSizes(card, slot) {
    const host = card.querySelector('[data-sizes]');
    if (!host) return;
    const current = this.sizeFor(slot);
    const values = this.sizesFor(slot);
    const key = `${this.styleOf(slot).handle}|${values.join(',')}`;
    if (host.dataset.key !== key) {
      host.dataset.key = key;
      host.replaceChildren(
        ...values.map((value) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'oj-pack__opt';
          btn.dataset.value = value;
          btn.setAttribute('aria-label', value);
          btn.textContent = sizeLabel(value);
          return btn;
        })
      );
    }
    const stock = this.inStock(slot);
    for (const btn of host.querySelectorAll('.oj-pack__opt')) {
      const out = !stock.has(btn.dataset.value);
      btn.setAttribute('aria-pressed', String(btn.dataset.value === current && !out));
      if (out) btn.setAttribute('aria-disabled', 'true');
      else btn.removeAttribute('aria-disabled');
    }

    // Say why no size is selected: the gate's size is sold out in this colour,
    // or a colour change cleared a size that colour doesn't have.
    const note = card.querySelector('[data-size-note]');
    if (note) {
      const colour = this.state.colour[slot] || '';
      let text = '';
      if (this.missing(slot)) text = `${sizeLabel(current)} is sold out in ${colour}. Choose another size.`;
      else if (!current && (this.needSize[slot] || Object.hasOwn(this.state.override, slot))) text = `Choose a size in ${colour}.`;
      note.hidden = !text;
      note.textContent = text;
    }
  }

  /* ---------- card choices ------------------------------------------ */

  /** Cycles this slot to the next style (Kenson -> Mallory -> Regent -> ...).
   *  The new style opens on its own starting colour in the gate's size. */
  swapStyle(slot) {
    if (this.styles.length < 2) return;
    this.state.style[slot] = (this.state.style[slot] + 1) % this.styles.length;
    delete this.state.colour[slot];
    delete this.state.override[slot];
    delete this.needSize[slot];
    this.showError('');
    this.paint();
    this.save();
  }

  pickColour(slot, name) {
    this.state.colour[slot] = name;
    // Colour is the customer's explicit choice here, so it stays; a size it
    // doesn't come in is cleared rather than silently kept.
    const size = this.sizeFor(slot);
    if (size && !this.inStock(slot).has(size)) this.state.override[slot] = '';
    this.paint();
    this.save();
  }

  /* ---------- painting ---------------------------------------------- */

  paint() {
    for (const opt of this.querySelectorAll('[data-gate-opts] .oj-pack__opt')) {
      opt.setAttribute('aria-pressed', String(opt.dataset.value === this.state.size));
    }

    const combo = this.combo();

    this.cards.forEach((card) => {
      const slot = Number(card.dataset.slot);
      const style = this.styleOf(slot);
      this.settleColour(slot);

      const name = card.querySelector('[data-name]');
      if (name && name.textContent !== style.label) name.textContent = style.label;
      this.paintRating(card, style);

      const isFree = combo ? combo.slot === slot : false;
      const tag = card.querySelector('[data-free-tag]');
      if (tag) tag.hidden = !isFree;
      this.paintPrice(card, style, isFree, tag?.textContent || 'Free');

      this.paintSwatches(card, slot);
      const colourName = card.querySelector('[data-colour-name]');
      if (colourName) colourName.textContent = this.state.colour[slot] || '';
      this.paintSizes(card, slot);
      this.paintGallery(card, slot);

      const swap = card.querySelector('[data-swap]');
      if (swap) {
        const next = this.styles[(this.state.style[slot] + 1) % this.styles.length];
        swap.setAttribute('aria-label', `Swap this polo for the ${next.label}`);
      }
    });

    if (combo) {
      // Full price of the three, then one saving (free polo + sale reductions),
      // then the total: full - saving = total, all server-formatted.
      this.setText('[data-ledger-sum]', combo.rrp);
      this.setText('[data-ledger-free]', `\u2212${combo.saving}`);
      this.setText('[data-ledger-total]', combo.total);
      this.setText('[data-bar-sum]', combo.rrp);
      this.setText('[data-bar-total]', combo.total);
    }
  }

  setText(selector, text) {
    const el = this.querySelector(selector);
    if (el && el.textContent !== text) el.textContent = text;
  }

  /** The server rendered each slot's starting polo's rating; this only redraws
   *  when Swap style has changed the polo. */
  paintRating(card, style) {
    const host = card.querySelector('[data-rating]');
    if (!host || host.dataset.handle === style.handle) return;
    host.dataset.handle = style.handle;
    const r = style.rating;
    host.hidden = !r;
    if (!r) {
      host.replaceChildren();
      return;
    }
    host.setAttribute('role', 'img');
    host.setAttribute('aria-label', `Rated ${r.avg} out of 5 from ${r.count} reviews`);
    const stars = document.createElement('span');
    stars.className = 'oj-pack__stars';
    stars.setAttribute('aria-hidden', 'true');
    for (let i = 1; i <= 5; i += 1) {
      const star = document.createElement('span');
      star.className = `oj-pack__star${i <= Math.round(r.avg) ? ' oj-pack__star--on' : ''}`;
      star.textContent = '\u2605';
      stars.append(star);
    }
    const text = document.createElement('span');
    text.setAttribute('aria-hidden', 'true');
    text.textContent = `${r.avg} (${r.count})`;
    host.replaceChildren(stars, text);
  }

  /** Server-formatted strings only: struck price + "Free" on the free slot,
   *  otherwise struck full price + selling price, as on the PDP. */
  paintPrice(card, style, isFree, freeLabel) {
    const host = card.querySelector('[data-price]');
    if (!host) return;
    const strike = (text) => {
      const el = document.createElement('s');
      el.textContent = text;
      return el;
    };
    const nodes = [];
    if (isFree) {
      nodes.push(strike(style.price));
      const f = document.createElement('span');
      f.className = 'oj-pack__price-free';
      f.textContent = freeLabel;
      nodes.push(f);
    } else {
      if (style.compare) nodes.push(strike(style.compare));
      nodes.push(document.createTextNode(style.price));
    }
    host.replaceChildren(...nodes);
  }

  paintSwatches(card, slot) {
    const host = card.querySelector('[data-swatches]');
    if (!host) return;
    // Every colour, always (operator, 2026-09-24). One with no stock in any
    // size is dimmed like the PDP's, but still selectable to look at.
    const options = this.styleOf(slot).colours || [];
    host.replaceChildren(
      ...options.map((c) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'oj-pack__swatch';
        btn.dataset.swatch = '';
        btn.dataset.colour = c.name;
        btn.title = c.name;
        btn.setAttribute('aria-label', c.name);
        btn.setAttribute('aria-pressed', String(c.name === this.state.colour[slot]));
        if (!this.inStock(slot, c.name).size) btn.dataset.soldOut = 'true';
        if (c.swatch) {
          const img = document.createElement('img');
          img.src = c.swatch;
          img.alt = '';
          img.loading = 'lazy';
          btn.append(img);
        }
        return btn;
      })
    );
  }

  /* ---------- gallery ------------------------------------------------ */

  nudge(card, dir) {
    const track = card.querySelector('[data-track]');
    track?.scrollBy({ left: dir * track.clientWidth, behavior: reduced() ? 'auto' : 'smooth' });
  }

  syncNav(card, track) {
    const prev = card.querySelector('[data-nav="-1"]');
    const next = card.querySelector('[data-nav="1"]');
    if (!prev || !next) return;
    const many = track.children.length > 1;
    const end = track.scrollWidth - track.clientWidth - 2;
    prev.hidden = !many || track.scrollLeft <= 2;
    next.hidden = !many || track.scrollLeft >= end;
  }

  paintGallery(card, slot) {
    const track = card.querySelector('[data-track]');
    if (!track) return;
    const style = this.styleOf(slot);
    const chosen = (style.colours || []).find((c) => c.name === this.state.colour[slot]);
    if (!chosen) return;

    const urls = [];
    for (const url of chosen.gallery || []) {
      if (url && !urls.some((u) => fileOf(u) === fileOf(url))) urls.push(url);
    }
    if (!urls.length) return;

    const key = `${style.handle}|${chosen.name}|${urls.length}`;
    if (track.dataset.key === key || track.dataset.pending === key) return;

    const existing = track.querySelector('img');
    const alt = `${style.label} polo, ${chosen.name}`;

    const commit = () => {
      if (this.styleOf(slot) !== style || this.state.colour[slot] !== chosen.name) return;
      track.replaceChildren(
        ...urls.map((url, n) => {
          const slide = document.createElement('div');
          slide.className = 'oj-pack__slide';
          let img;
          // Keep the server-rendered first frame when it is the same photograph:
          // a new element would throw away a decoded image.
          if (n === 0 && existing && fileOf(existing.currentSrc || existing.src) === fileOf(url)) {
            img = existing;
            img.alt = alt;
          } else {
            img = document.createElement('img');
            img.src = url;
            img.alt = n === 0 ? alt : '';
            img.loading = n === 0 ? 'eager' : 'lazy';
            img.decoding = 'async';
            img.width = 900;
            img.height = 1350;
          }
          slide.append(img);
          return slide;
        })
      );
      track.scrollLeft = 0;
      track.dataset.key = key;
      delete track.dataset.pending;
      if (!track.dataset.watched) {
        track.dataset.watched = 'true';
        track.addEventListener('scroll', () => this.syncNav(card, track), {
          passive: true,
          signal: this.abort.signal,
        });
      }
      this.syncNav(card, track);
    };

    if (existing && fileOf(existing.currentSrc || existing.src) === fileOf(urls[0])) return commit();

    // Hold the current photograph until the new one has decoded: never blank
    // the card, never leave the previous colour on it.
    track.dataset.pending = key;
    const next = new Image();
    next.onload = commit;
    next.onerror = () => delete track.dataset.pending;
    next.src = urls[0];
  }

  /* ---------- cart --------------------------------------------------- */

  showError(text) {
    if (!this.errorEl) return;
    this.errorEl.textContent = text;
    this.errorEl.hidden = !text;
  }

  async add() {
    if (this.busy) return;
    this.showError('');
    if (!this.state.size && !Object.keys(this.state.override).length) return this.openGate();

    const items = [];
    for (let slot = 0; slot < SLOTS; slot += 1) {
      const variant = this.resolve(slot);
      if (!variant) {
        // Take the customer to the one polo that still needs an answer.
        this.needSize[slot] = true;
        this.paint();
        this.cards[slot]?.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'center' });
        this.showError(`Choose a size for the ${this.styleOf(slot).label}.`);
        return;
      }
      const same = items.find((it) => it.id === variant.id);
      // Three identical polos go in as one line of 3, not three lines.
      if (same) same.quantity += 1;
      else items.push({ id: variant.id, quantity: 1 });
    }

    this.busy = true;
    this.addBtn?.setAttribute('aria-busy', 'true');

    try {
      const addUrl = window.Theme?.routes?.cart_add_url;
      const res = await fetch(addUrl ? `${addUrl}.js` : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) {
        this.showError(data.description || data.message || 'One of these has just sold out. Choose again and retry.');
        this.busy = false;
        this.addBtn?.removeAttribute('aria-busy');
        return;
      }
      // Straight to checkout (operator, 2026-09-24). Stay busy: the page is
      // leaving, and a second tap must not add the pack twice.
      window.location.assign('/checkout');
    } catch {
      this.showError('That didn’t go through. Please try again.');
      this.busy = false;
      this.addBtn?.removeAttribute('aria-busy');
    }
  }

}

if (!customElements.get('oj-polo-pack')) customElements.define('oj-polo-pack', OjPoloPack);
